import React, { Fragment, Component } from 'react';
import ReactDOMServer from 'react-dom/server';
import { connect } from 'react-redux';
import _ from 'lodash';
import { withRouter } from 'react-router';
import formatDate from 'date-fns/format';
import { parse } from 'date-fns';
import getFeatureDataKey from '../modules/getFeatureDataKey';
import defineHtmlMapMarker from '../helpers/HtmlMapMarker';
import Helper from '../helpers/util';
import styles from '../styles.json';
import { replaceCountryTags } from '../helpers/mapUtils';

import { setPrevalenceSelectedCountry } from '../actions/actionCreators';

let HtmlMapMarker;

const pushDiseaseToGTM = (disease, name) => {
	window.dataLayer = window.dataLayer || [];
	window.dataLayer.push({
		event: 'custom.event.disease_click',
		'custom.event.disease_name': _.upperFirst(disease),
		'custom.event.disease_country': name,
	});
};

class PrevalenceMap extends Component {
	constructor() {
		super();
		this.outbreakPopups = [];
		this.outbreakPills = [];
		this.australiaFeature = null;
		this.australianStatePills = [];
		this.stateFeatures = [];
		this.state = {
			mapAPILoaded: false,
			geoJSONLoaded: false,
			geoJSON: [],
		};
		this.autoPan = false;
	}

	componentDidMount() {
		if (_.get(window, 'google.maps', false)) {
			this.mapsLoaded();
		} else {
			Helper.pubsub.subscribe('googleMapsLoaded', () => {
				this.mapsLoaded();
			});
		}
	}

	componentDidUpdate(prevProps) {
		if (this.map) {
			window.google.maps.event.trigger(this.map, 'resize');
			this.updateCountryColours();
			this.updateOutbreakPills();
			this.updateAustralianStatePills();
			this.loadMapPosition(prevProps);

			// new
			if (this.props.prevalenceCountry.country) {
				this.addAndOpenPopup(this.props.prevalenceCountry.country, {
					lat: this.props.prevalenceCountry.lat,
					lng: this.props.prevalenceCountry.lng,
				});
			}
			// end new

			// console.warn(`${prevProps.selectedDisease} ${this.props.selectedDisease}`);
			if (prevProps.selectedDisease !== this.props.selectedDisease) {
				this.closeAllOpenPopups();
			}
		}
	}

	loadMapPosition(previousProps) {
		// If map changed active state
		if (this.map && previousProps.active !== this.props.active) {
			// If this map became active and the travelmap latlng exists
			if (this.props.active && this.props.travelMap) {
				setTimeout(() => {
					this.map.setCenter(this.props.travelMap.center);
					this.map.setZoom(this.props.travelMap.zoom);
				}, 0);
			}
		}
	}

	mapsLoaded() {
		HtmlMapMarker = defineHtmlMapMarker(HtmlMapMarker);
		this.setState({ mapAPILoaded: true }, () => {
			this.init();
		});
	}

	closeAllOpenPopups() {
		// Remove outbreak popups
		if (this.outbreakPopups && this.outbreakPopups.length) {
			Helper.pubsub.publish('barIndicator', { show: true });
			this.outbreakPopups.forEach((marker) => {
				marker.remove();
			});
			this.outbreakPopups = [];
		}

		// Close state pill popups
		if (this.australianStatePills && this.australianStatePills.length) {
			this.australianStatePills.forEach((pill) => pill.closePopup());
		}
	}

	filterGeoJSON(data) {
		const countryFeatures = { ...data };
		const stateFeatures = { ...data };

		let states = [];

		// only return county polygons which we have travel data for
		const features = data.features.filter((feature) => {
			if (feature.properties.STATE_CODE !== 'undefined') {
				states.push(feature);
				return false;
			}
			return true;
		});

		countryFeatures.features = features;
		stateFeatures.features = states;
		return { features: countryFeatures, states: stateFeatures };
	}

	updateOutbreakPills() {
		// If on vaxiplace
		if (window.vaxiplace) {
			// Remove all existing pills
			this.outbreakPills.forEach((pill) => pill.remove());
			this.outbreakPills = [];

			this.features.forEach((feature) => {
				const { lat, lng } = feature[this.featureKey].center;
				const latLng = new window.google.maps.LatLng(lat, lng);
				if (lat && lng) {
					const outbreaks = this.getOutbreaks(
						feature[this.featureKey].iso_a2,
						this.props.diseases[this.props.selectedDisease]
					);
					if (outbreaks.length > 0) {
						// Add pills to countries with outbreaks
						const country = this.props.countries.find((country) => country.iso === feature[this.featureKey].iso_a2);
						if (country) {
							let newPill = new HtmlMapMarker({
								data: {},
								html: ReactDOMServer.renderToStaticMarkup(
									<div className="outbreak-label-marker">{feature[this.featureKey].name}</div>
								),
								latlng: latLng,
								map: this.map,
								afterRender: () => {
									// newPill.onClick(() => {
									// 	const disease = this.props.diseases[this.props.selectedDisease];
									// 	const diseaseInfo = country.diseases.find(d => d.key === this.props.selectedDisease);
									// 	const prevalence = diseaseInfo ? diseaseInfo.prevalence : false;
									// 	this.openPopup(country, disease, prevalence, { lat, lng });
									// });
								},
							});
							this.outbreakPills.push(newPill);
						}
					}
				}
			});
		}
	}

	updateAustralianStatePills() {
		// If on vaxiplace
		if (window.vaxiplace) {
			// Remove all pills
			this.australianStatePills.forEach((pill) => pill.remove());
			this.australianStatePills = [];

			if (!this.props.states) {
				return;
			}

			this.props.states.forEach((state) => {
				const selectedDisease = this.props.diseases[this.props.selectedDisease];
				if (selectedDisease) {
					const relevantDiseases = state.diseases.filter((d) => d.name === selectedDisease.name);
					if (relevantDiseases.length) {
						const { lat, lng } = state;
						const latLng = new window.google.maps.LatLng(lat, lng);
						if (lat && lng) {
							const statePill = new HtmlMapMarker({
								type: 'ausStatePills',
								data: {},
								html: ReactDOMServer.renderToStaticMarkup(
									<div className="outbreak-label-marker outbreak-label-marker--state">{state.abbr}</div>
								),
								latlng: latLng,
								map: this.map,
								afterRender: () => {
									statePill.onClick(() => {
										this.map.setCenter(latLng);
										this.closeAllOpenPopups();
										statePill.openPopup({
											popupHtml: this.createStatePopupHtml(state, relevantDiseases),
											closeHtml: ReactDOMServer.renderToStaticMarkup(<div className="icon icon-close" />),
										});
										const diseaseName = this.props.selectedDisease.split('-').join(' ');
										pushDiseaseToGTM(diseaseName, `${state.abbr}, Australia`);
									});
								},
							});
							this.australianStatePills.push(statePill);
						}
					}
				}
			});
		}
	}

	getOutbreaks(countryCode, disease) {
		if (!this.props.outbreaks || !disease) {
			return [];
		}
		return this.props.outbreaks.filter(
			(outbreak) => outbreak.country_iso === countryCode && outbreak.disease === disease.name
		);
	}

	updateCountryColours() {
		// Disable colours on states
		this.stateFeatures.forEach((stateFeature) => {
			this.map.data.overrideStyle(stateFeature, {
				strokeOpacity: 0,
				fillOpacity: 0,
			});
		});

		this.features.forEach((feature) => {
			// Disable colours on countries
			this.map.data.overrideStyle(feature, {
				strokeOpacity: 0,
				fillOpacity: 0,
			});

			const country = this.props.countries.find((country) => country.iso === feature[this.featureKey].iso_a2);

			if (country) {
				const selectedDiseaseKey = this.props.selectedDisease;
				const countryDiseaseInfo = country.diseases.find((disease) => disease.key === selectedDiseaseKey);

				// Only colour if relevant disease info is found
				if (countryDiseaseInfo) {
					const colours = {
						high: '#FF7777',
						medium: '#FFD685',
						low: '#61E6AC',
						no_data: '#ffffff',
						yes: '#B662FF',
						no: '#b3b3b3',
					};

					let colour = colours.unranked;
					const countryDiseasePrevalence = countryDiseaseInfo.prevalence || 'unranked';
					colour = colours[countryDiseasePrevalence];

					if (country.iso === 'AU') {
						// If it's Australia, colour each state individually
						// State lines are required
						this.stateFeatures.forEach((stateFeature) => {
							this.map.data.overrideStyle(stateFeature, {
								fillColor: colour,
								strokeWeight: 2,
								strokeColor: colour,
								strokeOpacity: 0.4,
								fillOpacity: 0.3,
							});
						});
					} else {
						// Else, colour in the whole country
						this.map.data.overrideStyle(feature, {
							fillColor: colour,
							strokeWeight: 2,
							strokeColor: colour,
							strokeOpacity: 0.3,
							fillOpacity: 0.3,
						});
					}
				}
			}
		});
	}

	addAndOpenPopup = (country, { lat, lng }) => {
		this.closeAllOpenPopups();

		// Make sure everything that is required exists
		if (!country || !lat || !lng) {
			return;
		}

		// Add a marker
		const popupContent = this.createOutbreakPopupHtml(country);
		if (popupContent) {
			// Get marker center - move the country down a bit if on mobile
			const latLng =
				window.innerWidth < 1200
					? new window.google.maps.LatLng(lat + 15, lng)
					: new window.google.maps.LatLng(lat, lng);

			this.autoPan = true;
			// Center the map over the marker
			this.map.setCenter(latLng);
			this.props.savePrevalenceMap(latLng, this.map.getZoom());

			const newMarker = new HtmlMapMarker({
				data: {},
				html: ReactDOMServer.renderToStaticMarkup(<div />),
				latlng: latLng,
				map: this.map,
				onPopupClose: () => {
					this.closeAllOpenPopups();
					Helper.pubsub.publish('barIndicator', { show: true });
				},
				afterRender: () => {
					newMarker.openPopup({
						popupHtml: popupContent,
						closeHtml: ReactDOMServer.renderToStaticMarkup(<div className="icon icon-close" />),
					});
					Helper.pubsub.publish('barIndicator', { show: false });
				},
			});

			// Add to markers list
			this.outbreakPopups.push(newMarker);
			// Push disease to google tag manager
			const diseaseName = this.props.selectedDisease.split('-').join(' ');
			pushDiseaseToGTM(diseaseName, country.name);
		}
	};

	init() {
		if (this.state.mapAPILoaded && this.props.isLoading === false) {
			// Enable MAP on element
			this.map = new window.google.maps.Map(this.containerNode, {
				zoom: 3.3,
				center: { lat: 22.35111, lng: 78.66774 },
				gestureHandling: 'greedy',
				styles: styles.withCountryName,
				streetViewControl: false,
				mapTypeControl: false,
				fullscreenControl: false,
				minZoom: 3,
				restriction: {
					latLngBounds: {
						north: (Math.atan(Math.sinh(Math.PI)) * 180) / Math.PI,
						south: -((Math.atan(Math.sinh(Math.PI)) * 180) / Math.PI),
						west: -180,
						east: 180,
					},
					strictBounds: true,
				},
			});

			// save latest map position after movment
			window.google.maps.event.addListener(this.map, 'idle', () => {
				if (this.props.active) {
					if (!this.autoPan) {
						this.props.savePrevalenceMap(this.map.getCenter(), this.map.getZoom());
					} else {
						this.autoPan = false;
					}
				}
			});

			// Build our array of features
			this.features = [];
			this.map.data.addListener('addfeature', (event) => {
				if (!this.featureKey) {
					// no feature data key found already so find key that has iso data
					this.featureKey = getFeatureDataKey(event.feature);
				}

				if (event.feature[this.featureKey].STATE_CODE === undefined) {
					// Save all country features
					this.features.push(event.feature);
				} else {
					// Save all state features
					this.stateFeatures.push(event.feature);
				}

				// Save reference to our Australia feature
				if (event.feature[this.featureKey].iso_a2 === 'AU') {
					this.australiaFeature = event.feature;
				}
			});

			// Add all the applicable GeoJSON to the map
			const countryGeoJSON = this.filterGeoJSON(this.props.geoJSON);
			this.map.data.addGeoJson(countryGeoJSON.features);
			this.map.data.addGeoJson(countryGeoJSON.states);

			// Reset map colours
			this.map.data.setStyle({
				strokeOpacity: 0,
				fillOpacity: 0,
			});

			// Set the colours based on the current set of props
			this.updateCountryColours();

			// Add outbreak pills for selected disease
			this.updateOutbreakPills();

			// Australian state pills
			this.updateAustralianStatePills();

			// Listen to clicks on features
			this.map.data.addListener('click', (event) => {
				this.closeAllOpenPopups();

				// If a state was clicked on, use the australia feature
				// else use the clicked on feature
				const feature = event.feature[this.featureKey].STATE_CODE !== undefined ? this.australiaFeature : event.feature;

				const coords = feature[this.featureKey].center;
				const country = this.props.countries.find((c) => c.iso === feature[this.featureKey].iso_a2);

				// Ensure the country exists in our data
				if (!country) {
					return;
				}

				// Add and open a popup
				this.addAndOpenPopup(country, coords);
			});

			this.map.addListener('click', () => {
				this.closeAllOpenPopups();
			});
		}
	}

	getDiseaseTitle(prevalence) {
		const titles = {
			high: 'High',
			medium: 'Medium',
			low: 'Low',
			no_data: 'No data',
			yes: 'Country with known disease risk',
			no: 'Country with no known or low disease risk',
		};
		return titles[prevalence];
	}

	createStatePopupHtml(state, diseases) {
		return ReactDOMServer.renderToStaticMarkup(
			<Fragment>
				<div className="outbreak-popup-header" title={`${state.abbr}, Australia`}>
					{`${state.abbr}, Australia`}
				</div>
				<div className="outbreak-popup-alerts">
					<div className="outbreak-popup-alerts-item">
						{diseases.map((disease) => (
							<div className="disease-info disease-info--case" key={disease.name}>
								<div className="disease-info__title">{disease.name}</div>
								<div className="disease-info__description">
									<div className="case-count">{disease.case_number} Cases</div>
									<div className="reported-date">{disease.year} to date</div>
								</div>
								<div className="disease-info__link">
									<a data-lightbox="leavesite" target="_blank" rel="noopener noreferrer" href={disease.link}>
										{disease.link_copy}
									</a>
								</div>
							</div>
						))}
					</div>
				</div>
			</Fragment>
		);
	}

	createOutbreakPopupHtml(country) {
		const disease = this.props.diseases[this.props.selectedDisease];
		const diseaseInfo = country.diseases.find((d) => d.key === this.props.selectedDisease);
		const prevalence = diseaseInfo ? diseaseInfo.prevalence : false;

		const showDiseaseInfo = diseaseInfo;
		const showEyebrow = window.vaxiplace;
		const outbreaks = this.getOutbreaks(country.iso, disease);
		const showOutbreaks = window.vaxiplace && country && outbreaks.length > 0;

		if (!showDiseaseInfo && !showOutbreaks) {
			return false;
		}

		// Get the disease description ahead of time so we can check for null
		const diseaseDescription = _.get(disease, ['descriptions', prevalence], '');

		return ReactDOMServer.renderToStaticMarkup(
			<Fragment>
				<div className="outbreak-popup-header" title={country.name}>
					{country.name}
				</div>
				<div className="outbreak-popup-alerts">
					<div className="outbreak-popup-alerts-item">
						{showDiseaseInfo && (
							<div className="disease-info">
								{showEyebrow && (
									<div className="disease-info__eyebrow">{disease.is_ranked ? 'Disease Risk Level' : 'Prevalence'}</div>
								)}
								<div className="disease-info__title">
									<span className={`dot ${prevalence}`} />
									{this.getDiseaseTitle(prevalence)}
								</div>
								{diseaseDescription !== null && (
									<div className="disease-info__description">
										{replaceCountryTags(diseaseDescription, country.name)}
									</div>
								)}
							</div>
						)}
						{showOutbreaks &&
							this.getOutbreaks(country.iso, disease).map((outbreak, index) => (
								<div key={index} className="disease-info disease-info--outbreak">
									<div className="disease-info__eyebrow">Outbreak Alert</div>
									<div className="disease-info__title">
										<span className="title">{disease.name}</span>
										<div className="reported">
											Reported on {formatDate(parse(outbreak.date, 'yyyy-MM-dd', new Date()), 'do MMMM yyyy')}
										</div>
									</div>
									<div className="disease-info__description">{outbreak.description}</div>
									<div className="disease-info__link">
										<a data-lightbox="leavesite" target="_blank" rel="noopener noreferrer" href={outbreak.link}>
											{outbreak.link_copy}
										</a>
									</div>
								</div>
							))}
					</div>
				</div>
			</Fragment>
		);
	}

	render() {
		return (
			<div className={`map-container ${this.props.active ? 'is-active' : ''}`}>
				<div className="map" ref={(ref) => (this.containerNode = ref)} />
			</div>
		);
	}
}

function mapStateToProps(state, props) {
	return {
		features: state.json.geoJSON.features,
		geoJSON: state.json.geoJSON,
		isLoading: state.fetchDataLoading,
		routing: state.routing,
		featured: state.hoverFeatured,
		travelMap: state.map.travelMap,
		countries: state.json.data.prevalence.countries,
		diseases: state.json.data.prevalence.diseases,
		outbreaks: state.json.data.prevalence.outbreaks || [],
		selectedDisease: props.match.params.secondaryRoute,
		states: state.json.data.states,

		prevalenceCountry: state.prevalence.prevalenceCountry,
	};
}

function mapDispatchToProps(dispatch) {
	return {
		savePrevalenceMap: (center, zoom) => {
			dispatch({
				type: 'SAVE_PREVALENCE_MAP',
				center: center,
				zoom: zoom,
			});
		},
		setPrevalenceSelectedCountry: (country, lat, lng) => dispatch(setPrevalenceSelectedCountry(country, lat, lng)),
		dispatch,
	};
}

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(PrevalenceMap));
